import { type FormEvent, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "../api/client";
import { profileApi } from "../api/profile";
import type { ContactUpdate, Profile, Referee } from "../api/types";
import ProfileChat from "../components/ProfileChat";
import { Alert, Button, Card, Field, Input, Spinner, Textarea } from "../components/ui";

const PROFILE_KEY = ["profile"];

const EMPTY_CONTACT: ContactUpdate = {
  full_name: "",
  email: "",
  phone: "",
  location: "",
  linkedin_url: "",
  website_url: "",
};

type Mode = "chat" | "edit";

/**
 * Replaces hand-editing `00-Dashboard/My Profile.md`.
 *
 * Contact details are separate from the body on purpose: they feed the PDF's
 * page-2 running header and want validation, while the body is what every
 * prompt receives. Saving the body bumps the profile version, which marks
 * generated documents stale; saving contact details does not.
 *
 * The body can be built by talking to Claude or edited by hand — the same
 * field either way.
 */
export default function ProfilePage() {
  const [mode, setMode] = useState<Mode>("chat");
  const queryClient = useQueryClient();
  const { data: profile, isPending } = useQuery({
    queryKey: PROFILE_KEY,
    queryFn: profileApi.read,
  });

  if (isPending || !profile) {
    return (
      <div className="py-10">
        <Spinner label="Loading your profile…" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold text-gray-900">Your profile</h1>
        <p className="mt-1 text-sm text-gray-600">
          Everything here is sent to Claude when generating a resume or cover letter. The more
          detail — especially quantified achievements — the better the result.
        </p>
      </header>

      <ContactCard
        profile={profile}
        onSaved={(next) => queryClient.setQueryData(PROFILE_KEY, next)}
      />

      <RefereesCard
        profile={profile}
        onSaved={(next) => queryClient.setQueryData(PROFILE_KEY, next)}
      />

      <div className="flex flex-wrap gap-1 border-b border-gray-200 pb-3">
        {(["chat", "edit"] as Mode[]).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setMode(option)}
            className={
              "rounded-md px-3 py-1.5 text-sm font-medium transition-colors " +
              (mode === option
                ? "bg-accent-50 text-accent-700"
                : "text-gray-600 hover:bg-gray-100")
            }
          >
            {option === "chat" ? "Build it with Claude" : "Edit directly"}
          </button>
        ))}
      </div>

      {mode === "chat" ? (
        <ProfileChat />
      ) : (
        <BodyCard
          profile={profile}
          onSaved={(next) => queryClient.setQueryData(PROFILE_KEY, next)}
        />
      )}
    </div>
  );
}

function ContactCard({
  profile,
  onSaved,
}: {
  profile: Profile;
  onSaved: (next: Profile) => void;
}) {
  const [form, setForm] = useState<ContactUpdate>(EMPTY_CONTACT);

  useEffect(() => {
    setForm({
      full_name: profile.full_name,
      email: profile.email,
      phone: profile.phone,
      location: profile.location,
      linkedin_url: profile.linkedin_url,
      website_url: profile.website_url,
    });
  }, [profile]);

  const save = useMutation({
    mutationFn: profileApi.updateContact,
    onSuccess: onSaved,
  });

  function set<K extends keyof ContactUpdate>(key: K, value: string) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    save.mutate(form);
  }

  return (
    <Card title="Contact details">
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Full name">
            <Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
          </Field>
          <Field label="Email">
            <Input
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
            />
          </Field>
          <Field label="Phone">
            <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          </Field>
          <Field label="Location">
            <Input value={form.location} onChange={(e) => set("location", e.target.value)} />
          </Field>
          <Field label="LinkedIn URL">
            <Input
              value={form.linkedin_url}
              onChange={(e) => set("linkedin_url", e.target.value)}
            />
          </Field>
          <Field label="Website">
            <Input
              value={form.website_url}
              onChange={(e) => set("website_url", e.target.value)}
            />
          </Field>
        </div>

        <p className="text-xs text-gray-500">
          These head your resume and cover letter exactly as typed here, and sign off the cover
          letter. Page two of each PDF carries a shorter running header:{" "}
          <span className="font-mono text-gray-700">{profile.header_line || "—"}</span>
        </p>

        <div className="flex items-center gap-3">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save contact details"}
          </Button>
          {save.isSuccess && <span className="text-sm text-accent-600">Saved.</span>}
        </div>
      </form>
    </Card>
  );
}

const MAX_REFEREES = 6;
const EMPTY_REFEREE: Referee = { name: "", position: "", phone: "", email: "" };

/** The referees printed at the foot of every resume, as typed here. */
function RefereesCard({
  profile,
  onSaved,
}: {
  profile: Profile;
  onSaved: (next: Profile) => void;
}) {
  const [rows, setRows] = useState<Referee[]>(profile.referees);

  useEffect(() => setRows(profile.referees), [profile.referees]);

  const save = useMutation({
    // A row with no name is an unfinished one; it isn't sent.
    mutationFn: () => profileApi.updateReferees(rows.filter((row) => row.name.trim())),
    onSuccess: onSaved,
  });

  function set(index: number, key: keyof Referee, value: string) {
    setRows((current) => current.map((row, i) => (i === index ? { ...row, [key]: value } : row)));
  }

  const dirty = JSON.stringify(rows) !== JSON.stringify(profile.referees);
  const error = save.error instanceof ApiError ? save.error.message : null;

  return (
    <Card title="References">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Listed at the end of every resume, exactly as typed. Leave this empty to leave the
          section out.
        </p>

        {error && <Alert kind="error">{error}</Alert>}

        {rows.map((row, index) => (
          <fieldset key={index} className="space-y-3 rounded-md border border-gray-200 p-4">
            <legend className="sr-only">Referee {index + 1}</legend>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name">
                <Input value={row.name} onChange={(e) => set(index, "name", e.target.value)} />
              </Field>
              <Field label="Position">
                <Input
                  value={row.position}
                  placeholder="Engineering Director, Acme"
                  onChange={(e) => set(index, "position", e.target.value)}
                />
              </Field>
              <Field label="Phone">
                <Input value={row.phone} onChange={(e) => set(index, "phone", e.target.value)} />
              </Field>
              <Field label="Email">
                <Input
                  type="email"
                  value={row.email}
                  onChange={(e) => set(index, "email", e.target.value)}
                />
              </Field>
            </div>
            <button
              type="button"
              onClick={() => setRows((current) => current.filter((_, i) => i !== index))}
              className="text-sm text-red-600 hover:underline"
            >
              Remove
            </button>
          </fieldset>
        ))}

        <div className="flex flex-wrap items-center gap-3">
          {rows.length < MAX_REFEREES && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => setRows((current) => [...current, { ...EMPTY_REFEREE }])}
            >
              Add a referee
            </Button>
          )}
          <Button type="button" onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
            {save.isPending ? "Saving…" : "Save references"}
          </Button>
          {save.isSuccess && !dirty && <span className="text-sm text-accent-600">Saved.</span>}
        </div>
      </div>
    </Card>
  );
}

function BodyCard({ profile, onSaved }: { profile: Profile; onSaved: (next: Profile) => void }) {
  const [body, setBody] = useState(profile.body_md);

  useEffect(() => setBody(profile.body_md), [profile.body_md]);

  const save = useMutation({
    mutationFn: () => profileApi.updateBody(body),
    onSuccess: onSaved,
  });

  const dirty = body !== profile.body_md;

  return (
    <Card title="Background">
      <div className="space-y-4">
        <Textarea
          rows={22}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={
            "# Summary\n\n…\n\n# Skills\n\n…\n\n# Experience\n\n## Role, Company (2021 – present)\n\n- Achievement with a number in it\n"
          }
        />

        {dirty && (
          <Alert kind="error">
            Saving a change here marks every generated resume and cover letter stale, so you can
            regenerate them against the new profile.
          </Alert>
        )}

        <div className="flex items-center gap-3">
          <Button type="button" onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
            {save.isPending ? "Saving…" : "Save background"}
          </Button>
          <span className="text-xs text-gray-500">Version {profile.version}</span>
        </div>
      </div>
    </Card>
  );
}
